import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';

import { BlockTextComponent } from '../../common/block-text/block-text.component';
import { FieldEditorV2Component } from '../../base-template-editor-v2/field-editor-v2/field-editor-v2.component';
import { InlineEditV2Directive } from '../../base-template-editor-v2/inline-edit/inline-edit-v2.directive';
import { InlineMediaV2Directive } from '../../base-template-editor-v2/inline-edit/inline-media-v2.directive';
import { BaseComponent } from '../../common/BaseComponent';
import { Helper } from '../../../../utils/helper';
import {
  ZigZagHeadingAndCtaInitModel,
  ZigZagHeadingAndCtaInitVariables,
  ZigZagHeadingAndCtaModel,
  ZigZagHeadingAndCtaVariables,
} from '../../../../models/email-template/zigzag-heading-and-cta.interface';

/**
 * Mockup-styled ZigZag Heading + Primary CTA block - a drop-in replacement
 * for app-zigzag-heading-and-cta in block-renderer-v2 (same
 * [block]/[isPreview] inputs and outputs), so it covers both the
 * center-canvas summary (isPreview=true) and the right-panel edit
 * form (isPreview=false). Reuses the real, unmodified app-block-text (image
 * upload/segment/validation) and app-field-editor-v2/app-pin sub-components
 * and the exact same ZigZagHeadingAndCtaVariables field names - only the
 * surrounding layout is new. Image always renders on the left (matches v1's
 * fixed layout exactly - the mockup's own automatic left/right alternation
 * across consecutive zigzag blocks is a demo-only flourish v1 never had).
 */
@Component({
  selector: 'app-zigzag-heading-and-cta-v2',
  imports: [CommonModule, ReactiveFormsModule, BlockTextComponent, FieldEditorV2Component, InlineEditV2Directive, InlineMediaV2Directive],
  templateUrl: './zigzag-heading-and-cta-v2.component.html',
  styleUrl: './zigzag-heading-and-cta-v2.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ZigZagHeadingAndCtaComponentV2 extends BaseComponent implements OnInit {
  @Input() block: ZigZagHeadingAndCtaModel = new ZigZagHeadingAndCtaInitModel();
  @Input() isPreview = false;
  @Input() disabled = false;

  @Output() blockData = new EventEmitter<any>();
  @Output() removeBlockEvent = new EventEmitter<string | null>();

  ngOnInit() {
    if (Helper.isEmpty(this.block.variables!)) {
      this.block.variables = new ZigZagHeadingAndCtaInitVariables();
    }
  }

  imagePlaceholder = 'Image';
  imageHrefPlaceholder = 'Enter Image Redirect Link';
  imageAltPlaceholder = 'Enter Image Alt Text';
  headingPlaceholder = 'Enter Heading';
  textPlaceholder = 'Enter Text';
  ctaLinkPlaceholder = 'Enter CTA Link';
  ctaTextPlaceholder = 'Enter CTA Text';

  protected readonly ctaButtonPlaceholder = 'Insert CTA text';

  protected imageAsUpload = true;

  protected toggleImageMode(): void {
    this.imageAsUpload = !this.imageAsUpload;
  }

  get imagePreviewHref(): string {
    const base = this.helper.getFullUrl(this.block.variables?.['ZigZagHacImageHref']);
    const segment = this.block.variables?.['ZigZagHacImageHrefSegmentRegex'];
    if (!segment) {
      return base;
    }
    const separator = base.includes('?') ? '&' : '?';
    return `${base}${separator}segment=${encodeURIComponent(segment)}`;
  }

  get ctaPreviewHref(): string {
    const base = this.helper.getFullUrl(this.block.variables?.['ZigZagHacCtaLink']);
    const segment = this.block.variables?.['ZigZagHacCtaLinkSegmentRegex'];
    if (!segment) {
      return base;
    }
    const separator = base.includes('?') ? '&' : '?';
    return `${base}${separator}segment=${encodeURIComponent(segment)}`;
  }

  removeBlock(): void {
    this.removeBlockEvent.emit(this.block.blockId);
  }

  onChange(value: any, fieldName: keyof ZigZagHeadingAndCtaVariables) {
    if (this.block.variables) {
      (this.block.variables as any)[fieldName] = value;
      this.blockData.emit(this.block);
    }
  }


  handleOnSegmentChange(segment: string, blockName: string) {
    if (this.block.variables) {
      (this.block.variables as any)[blockName] = segment;
      this.blockData.emit(this.block);
    }
  }
}
